import { useState, useRef, useEffect, useCallback } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { cn } from '../../utils';
import './AiAssistantInstructionPopup.css';

function getStorageKey(userLogin: string): string {
  const safe = userLogin.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `engyme_ai_assistant_instruction_v1_${safe}`;
}

function readDismissed(userLogin: string): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(getStorageKey(userLogin)) === '1';
}

export interface AiAssistantInstructionPopupProps {
  userLogin: string;
}

export function AiAssistantInstructionPopup({ userLogin }: AiAssistantInstructionPopupProps) {
  const [dismissed, setDismissed] = useState(() => readDismissed(userLogin));
  const [phase, setPhase] = useState<'closed' | 'open' | 'closing'>(() =>
    readDismissed(userLogin) ? 'closed' : 'open'
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const d = readDismissed(userLogin);
    setDismissed(d);
    setPhase(d ? 'closed' : 'open');
  }, [userLogin]);

  const finishClose = useCallback(() => {
    localStorage.setItem(getStorageKey(userLogin), '1');
    setDismissed(true);
    setPhase('closed');
  }, [userLogin]);

  const runCloseAnimation = useCallback(() => {
    const container = containerRef.current;
    const panel = panelRef.current;
    if (!container || !panel) {
      finishClose();
      return;
    }

    setPhase('closing');
    backdropRef.current?.classList.add('ai-assistant-instruction__backdrop--out');
    const pr = panel.getBoundingClientRect();
    const cr = container.getBoundingClientRect();
    const fabSize = 48;
    const margin = 16;
    const fabCx = cr.right - margin - fabSize / 2;
    const fabCy = cr.bottom - margin - fabSize / 2;
    const pcx = pr.left + pr.width / 2;
    const pcy = pr.top + pr.height / 2;
    const dx = fabCx - pcx;
    const dy = fabCy - pcy;
    let scale = Math.min(fabSize / pr.width, fabSize / pr.height);
    scale = Math.min(Math.max(scale, 0.07), 0.42);

    panel.style.setProperty('--collapse-dx', `${dx}px`);
    panel.style.setProperty('--collapse-dy', `${dy}px`);
    panel.style.setProperty('--collapse-s', String(scale));

    requestAnimationFrame(() => {
      panel.classList.add('ai-assistant-instruction__panel--collapsing');
    });
  }, [finishClose]);

  const handlePanelTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      // Игнорируем всплытие с кнопок (transform :active у «Понятно» и т.п.)
      if (e.target !== e.currentTarget) return;
      if (phase !== 'closing') return;
      if (e.propertyName !== 'transform' && e.propertyName !== '-webkit-transform') return;
      const panel = panelRef.current;
      if (panel) {
        panel.classList.remove('ai-assistant-instruction__panel--collapsing');
        panel.style.removeProperty('--collapse-dx');
        panel.style.removeProperty('--collapse-dy');
        panel.style.removeProperty('--collapse-s');
      }
      backdropRef.current?.classList.remove('ai-assistant-instruction__backdrop--out');
      finishClose();
    },
    [phase, finishClose]
  );

  const openFromFab = useCallback(() => {
    setPhase('open');
  }, []);

  const handleDismiss = useCallback(() => {
    runCloseAnimation();
  }, [runCloseAnimation]);

  useEffect(() => {
    if (phase !== 'open' && phase !== 'closing') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') runCloseAnimation();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, runCloseAnimation]);

  const overlayActive = phase === 'open' || phase === 'closing';
  const fabVisible = dismissed && phase === 'closed';

  return (
    <div ref={containerRef} className="ai-assistant-instruction-root" aria-hidden={!overlayActive && !fabVisible}>
      {overlayActive && (
        <div ref={backdropRef} className="ai-assistant-instruction__backdrop" aria-hidden={false} />
      )}

      {overlayActive && (
        <div
          className="ai-assistant-instruction__wrap"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-assistant-instruction-title"
        >
          <div
            ref={panelRef}
            className={cn(
              'ai-assistant-instruction__panel',
              phase === 'open' && 'ai-assistant-instruction__panel--in'
            )}
            onTransitionEnd={handlePanelTransitionEnd}
          >
            <div className="ai-assistant-instruction__head">
              <div className="ai-assistant-instruction__head-text">
                <p className="ai-assistant-instruction__eyebrow">Как работать с AI-ассистентом</p>
                <h2 id="ai-assistant-instruction-title" className="ai-assistant-instruction__title">
                  Загрузите спецификацию и выберите нужный тип отчёта
                </h2>
              </div>
              <button
                type="button"
                className="ai-assistant-instruction__icon-btn"
                onClick={handleDismiss}
                aria-label="Закрыть"
              >
                <X size={22} strokeWidth={2} />
              </button>
            </div>

            <div className="ai-assistant-instruction__body">
              <div className="ai-assistant-instruction__col">
                <p className="ai-assistant-instruction__section-label">Базовый сценарий</p>
                <ol className="ai-assistant-instruction__steps">
                  <li className="ai-assistant-instruction__step">
                    <span className="ai-assistant-instruction__step-num" aria-hidden>
                      1
                    </span>
                    <div>
                      <strong className="ai-assistant-instruction__step-title">Подготовьте файл</strong>
                      <p className="ai-assistant-instruction__step-text">
                        Уберите лишние данные. В спецификации должны остаться в основном технические параметры.
                      </p>
                    </div>
                  </li>
                  <li className="ai-assistant-instruction__step">
                    <span className="ai-assistant-instruction__step-num" aria-hidden>
                      2
                    </span>
                    <div>
                      <strong className="ai-assistant-instruction__step-title">Выберите режим</strong>
                      <p className="ai-assistant-instruction__step-text">
                        Напишите в чат одну из команд: <strong>Отчёт 1</strong>, <strong>Отчёт 2</strong> или{' '}
                        <strong>Отчёт 3</strong>.
                      </p>
                    </div>
                  </li>
                  <li className="ai-assistant-instruction__step">
                    <span className="ai-assistant-instruction__step-num" aria-hidden>
                      3
                    </span>
                    <div>
                      <strong className="ai-assistant-instruction__step-title">Получите результат</strong>
                      <p className="ai-assistant-instruction__step-text">
                        AI-ассистент вернёт аналитику, список недостающих параметров или таблицу характеристик — в
                        зависимости от режима.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="ai-assistant-instruction__col">
                <p className="ai-assistant-instruction__section-label">Режимы работы</p>
                <ul className="ai-assistant-instruction__modes">
                  <li>
                    <strong>Отчёт 1 — анализ спецификации</strong>
                    <span>Показывает, каких параметров не хватает для подбора и проектирования оборудования.</span>
                  </li>
                  <li>
                    <strong>Отчёт 2 — подбор аналогов</strong>
                    <span>Помогает подобрать аналоги и отдельно сообщает, каких данных для этого не хватает.</span>
                  </li>
                  <li>
                    <strong>Отчёт 3 — таблица для ТЗ</strong>
                    <span>Собирает найденные характеристики в таблицу для технического задания или ТУ.</span>
                  </li>
                </ul>
                <div className="ai-assistant-instruction__warn">
                  <strong className="ai-assistant-instruction__warn-title">Большие спецификации</strong>
                  <p className="ai-assistant-instruction__warn-text">
                    При необходимости разбивайте файл на части (примерно до 50 строк) и группируйте по однотипному
                    оборудованию.
                  </p>
                </div>
                <p className="ai-assistant-instruction__disclaimer">
                  Проверяйте ответы: AI-ассистент может ошибаться.
                </p>
              </div>
            </div>

            <div className="ai-assistant-instruction__foot">
              <button type="button" className="ai-assistant-instruction__btn-primary" onClick={handleDismiss}>
                Понятно
              </button>
            </div>
          </div>
        </div>
      )}

      {fabVisible && (
        <button
          type="button"
          className="ai-assistant-instruction__fab"
          onClick={openFromFab}
          aria-label="Как работать с AI-ассистентом"
          title="Как работать с AI-ассистентом"
        >
          <HelpCircle size={26} strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
}
